import { useWindowWidth } from '@react-hook/window-size'
import { useEffect, useMemo, useState } from 'react'

export enum Breakpoint {
  NONE = 0,
  XS = 0.5,
  SM = 1,
  MD = 2,
  LG = 3,
  XL = 4,
  XL2 = 5,
}

export function getBreakpoint(width: number): Breakpoint {
  if (width >= 1536) {
    return Breakpoint.XL2
  } else if (width >= 1280) {
    return Breakpoint.XL
  } else if (width >= 1024) {
    return Breakpoint.LG
  } else if (width >= 768) {
    return Breakpoint.MD
  } else if (width >= 640) {
    return Breakpoint.SM
  } else if (width >= 320) {
    return Breakpoint.XS
  } else {
    return Breakpoint.NONE
  }
}

export function useBreakpoint(): Breakpoint {
  const width = useWindowWidth()
  const [breakpoint, setBreakpoint] = useState(Breakpoint.NONE)

  useEffect(() => {
    setBreakpoint(getBreakpoint(width))
  }, [width])

  return breakpoint
}

export function useCheckBreakpoint(condition: { lt: Breakpoint } | { gt: Breakpoint }): boolean {
  const br = useBreakpoint()

  if ('lt' in condition) {
    return br < condition.lt
  }
  if ('gt' in condition) {
    return br >= condition.gt
  }
  return false
}

type MediaType = 'NONE' | 'XS' | 'SM' | 'LG' | 'XL' | 'XL2'

interface MediaQuery {
  [key: string]: string
}

export const useMediaQuery = (): { [key: string]: boolean | undefined } => {
  const queries: MediaQuery = useMemo(
    () => ({
      NONE: '(max-width: 319px)',
      XS: '(min-width: 320px) and (max-width: 639px)',
      SM: '(min-width: 640px) and (max-width: 767px)',
      MD: '(min-width: 768px) and (max-width: 1023px)',
      LG: '(min-width: 1024px) and (max-width: 1279px)',
      XL: '(min-width: 1280px) and (max-width: 1535px)',
      XL2: '(min-width: 1536px)',
    }),
    [],
  )
  const win = typeof window === 'undefined' ? null : window
  const [matches, setMatches] = useState<Array<{ [key: string]: boolean | undefined }>>(() => {
    const mediaQueryLists: Array<{ [key: string]: MediaQueryList | null }> = Object.keys(queries).map((query) => {
      return { [query]: win?.matchMedia(queries[query]) || null }
    })
    return mediaQueryLists.map((mediaQuery) => {
      const key = Object.keys(mediaQuery)[0]
      return { [key]: mediaQuery[key]?.matches }
    })
  })

  useEffect(() => {
    const mediaQueryLists: Array<{ [key: string]: MediaQueryList | null }> = Object.keys(queries).map((query) => {
      return { [query]: win?.matchMedia(queries[query]) || null }
    })
    const listener = (): void => {
      setMatches(
        mediaQueryLists.map((mediaQuery) => {
          const key = Object.keys(mediaQuery)[0]
          return { [key]: mediaQuery[key]?.matches }
        }),
      )
    }
    mediaQueryLists.forEach((mediaQuery) => {
      const key = Object.keys(mediaQuery)[0]
      mediaQuery[key]?.addListener(listener)
    })
    return () => {
      mediaQueryLists.forEach((mediaQuery) => {
        const key = Object.keys(mediaQuery)[0]
        mediaQuery[key]?.removeListener(listener)
      })
    }
  }, [queries, win])

  return matches.find((obj: { [key: string]: boolean | undefined }) => Object.values(obj)[0]) || {}
}

export const useScreenBreakPoints = (
  condition: { lt: Breakpoint; gt?: undefined } | { lt?: undefined; gt: Breakpoint },
): boolean => {
  const matches = useMediaQuery()

  const key = Object.keys(matches)[0] as MediaType
  const value = Breakpoint[key]
  if (typeof condition?.lt !== 'undefined') {
    return condition.lt > value
  }

  if (typeof condition?.gt !== 'undefined') {
    return condition.gt <= value
  }

  return false
}
